import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ExternalLink, X } from "lucide-react";
import { cn } from "@/lib/cn";
import {
  getBillingStatus,
  type BillingLevel,
  type BillingProviderState,
  type BillingStatus,
} from "@/lib/api";

/**
 * §E3 / §C2 — credit-balance warning banner (OpenAI + Gemini).
 *
 * Rosie's agents stop responding the moment the OpenAI balance
 * hits zero OR the Gemini budget gets exhausted (whichever the
 * operator is routing chat through). Akil needs to SEE that
 * coming before a customer call gets cut off. The banner polls
 * `/api/billing/status` every 60s and renders a stacked warning
 * above every admin page.
 *
 * Levels match the backend `BillingLevel` union:
 *   ok       → nothing
 *   warning  → orange bar, dismissable for 10 min
 *   critical → red bar, dismissable for 10 min
 *   depleted → hardest red + `aria-live=assertive` + NOT dismissable.
 *              Only OpenAI can reach this level today; Gemini caps
 *              at `critical` because Google has no admin cost API.
 *
 * When BOTH providers are above `ok`, the banner shows the worse
 * one first and mentions the other in a second line — operator
 * doesn't have to click to see the full picture. The dismiss
 * button snoozes both providers together (their dismissals track
 * the worst-level key). Re-reads every 60s so the banner
 * reappears once the snooze window closes.
 */
const OPENAI_TOPUP_URL =
  "https://platform.openai.com/account/billing/overview";
const GEMINI_TOPUP_URL = "https://aistudio.google.com/app/billing";

const CRITICAL_SNOOZE_MS = 10 * 60 * 1000; // 10 minutes
const POLL_INTERVAL_MS = 60 * 1000; // 60 seconds
const STALE_TIME_MS = 30 * 1000;

const DISMISS_KEY_PREFIX = "credit-balance-banner:dismissed:";

const LEVEL_PRIORITY: Record<BillingLevel, number> = {
  ok: 0,
  warning: 1,
  critical: 2,
  depleted: 3,
};

type ProviderId = "openai" | "gemini";

function formatUsd(value: number): string {
  if (!Number.isFinite(value)) return "$0.00";
  if (value >= 100) return `$${value.toFixed(0)}`;
  return `$${value.toFixed(2)}`;
}

function providerLabel(id: ProviderId): string {
  return id === "openai" ? "OpenAI" : "Gemini";
}

function providerTopup(id: ProviderId): string {
  return id === "openai" ? OPENAI_TOPUP_URL : GEMINI_TOPUP_URL;
}

interface ProviderSummary {
  id: ProviderId;
  state: BillingProviderState;
}

/**
 * Return the providers that need attention, ordered worst → least
 * worst. Exported for unit tests.
 */
export function pickActiveProviders(
  status: BillingStatus | undefined,
): ProviderSummary[] {
  if (!status) return [];
  const entries: ProviderSummary[] = [{ id: "openai", state: status.openai }];
  if (status.gemini) entries.push({ id: "gemini", state: status.gemini });
  return entries
    .filter(({ state }) => state.level !== "ok")
    .sort(
      (a, b) => LEVEL_PRIORITY[b.state.level] - LEVEL_PRIORITY[a.state.level],
    );
}

function bannerCopy(
  id: ProviderId,
  state: BillingProviderState,
): {
  headline: string;
  body: string;
  cta: string;
} {
  const label = providerLabel(id);
  const remainingUsd = Math.max(0, state.limit - state.used);
  const remainingPct = Math.max(
    0,
    Math.round(state.remaining_fraction * 100),
  );

  if (state.blocks_new_sessions || state.level === "depleted") {
    return {
      headline: `${label} credits depleted — agents are offline`,
      body: state.action_message
        ? state.action_message
        : `New voice and chat sessions will fail until you top up your ${label} balance.`,
      cta: `Top up ${label}`,
    };
  }
  if (state.level === "critical") {
    return {
      headline: `Only ${formatUsd(remainingUsd)} of ${label} credit left (${remainingPct}%)`,
      body:
        state.action_message ||
        "Agents will stop responding when this runs out. Top up before your next peak window.",
      cta: `Top up ${label}`,
    };
  }
  return {
    headline: `${label} credit is running low — ${formatUsd(remainingUsd)} left (${remainingPct}%)`,
    body:
      state.action_message ||
      "You have headroom, but plan a top-up so customer replies don't fail mid-conversation.",
    cta: `Top up ${label}`,
  };
}

function levelToneClasses(level: BillingLevel, blocked: boolean): string {
  if (blocked || level === "depleted") {
    return "bg-danger-bg text-danger-fg border-danger-border";
  }
  if (level === "critical") {
    return "bg-danger-bg/60 text-danger-fg border-danger-border";
  }
  return "bg-warning-bg text-warning-fg border-warning-border";
}

export interface BannerVisibility {
  visible: boolean;
  dismissible: boolean;
  primary: ProviderSummary | null;
  secondary: ProviderSummary | null;
}

/**
 * Decide banner visibility given the raw status + dismissal record.
 * Pure so we can unit-test.
 */
export function shouldShowBanner(
  status: BillingStatus | undefined,
  dismissedUntilUnix: number | null,
  nowUnix: number,
): BannerVisibility {
  const active = pickActiveProviders(status);
  if (active.length === 0) {
    return { visible: false, dismissible: false, primary: null, secondary: null };
  }
  const primary = active[0];
  const secondary = active[1] ?? null;
  const blocked =
    primary.state.blocks_new_sessions || primary.state.level === "depleted";
  if (blocked) {
    return { visible: true, dismissible: false, primary, secondary };
  }
  // warning/critical → respect snooze.
  if (dismissedUntilUnix !== null && nowUnix < dismissedUntilUnix) {
    return { visible: false, dismissible: true, primary, secondary };
  }
  return { visible: true, dismissible: true, primary, secondary };
}

/** Dismissal key tracks the worst-level in scope so a fresh,
 *  worse level clears its own dismissal automatically. */
function dismissKey(level: BillingLevel): string {
  return `${DISMISS_KEY_PREFIX}${level}`;
}

function readDismissedUntil(level: BillingLevel): number | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(dismissKey(level));
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function writeDismissedUntil(level: BillingLevel, untilUnix: number): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(dismissKey(level), String(untilUnix));
  } catch {
    // Private browsing / storage full — fail open: banner stays visible.
  }
}

export function CreditBalanceBanner() {
  const { data } = useQuery<BillingStatus>({
    queryKey: ["billing", "status"],
    queryFn: getBillingStatus,
    refetchInterval: POLL_INTERVAL_MS,
    staleTime: STALE_TIME_MS,
    retry: 1,
  });

  // Force re-render so dismissal expiry re-evaluates without a
  // full refetch.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 60_000);
    return () => window.clearInterval(id);
  }, []);

  const active = pickActiveProviders(data);
  // Dismissal is keyed by the current worst level — escalating to
  // a higher level clears the snooze automatically.
  const worstLevel: BillingLevel = active[0]?.state.level ?? "ok";
  const dismissedUntil = readDismissedUntil(worstLevel);
  const now = Math.floor(Date.now() / 1000);
  const { visible, dismissible, primary, secondary } = shouldShowBanner(
    data,
    dismissedUntil,
    now,
  );

  if (!visible || !primary) return null;

  const copy = bannerCopy(primary.id, primary.state);
  const blocked =
    primary.state.blocks_new_sessions || primary.state.level === "depleted";

  const onDismiss = () => {
    const snoozeUntilUnix =
      Math.floor(Date.now() / 1000) + Math.floor(CRITICAL_SNOOZE_MS / 1000);
    writeDismissedUntil(worstLevel, snoozeUntilUnix);
    setTick((t) => t + 1);
  };

  const secondaryLine = secondary
    ? (() => {
        const c = bannerCopy(secondary.id, secondary.state);
        return c.headline;
      })()
    : null;

  return (
    <div
      role="alert"
      aria-live={blocked ? "assertive" : "polite"}
      data-testid="credit-balance-banner"
      data-level={primary.state.level}
      data-primary-provider={primary.id}
      data-secondary-provider={secondary?.id ?? ""}
      className={cn(
        "flex items-start gap-3 border-b px-6 py-3 text-sm",
        levelToneClasses(primary.state.level, blocked),
      )}
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <div className="flex-1 min-w-0">
        <div className="font-semibold">{copy.headline}</div>
        <div className="mt-0.5 opacity-90">{copy.body}</div>
        {secondaryLine ? (
          <div
            className="mt-1 text-xs opacity-80"
            data-testid="credit-balance-banner-secondary"
          >
            Also: {secondaryLine}
          </div>
        ) : null}
      </div>
      <a
        href={providerTopup(primary.id)}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          "inline-flex items-center gap-1 rounded-md border border-current px-3 py-1",
          "text-xs font-medium hover:bg-white/10",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring",
        )}
      >
        {copy.cta}
        <ExternalLink className="h-3 w-3" aria-hidden />
      </a>
      {dismissible ? (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss credit warning for 10 minutes"
          data-testid="credit-balance-banner-dismiss"
          className={cn(
            "rounded-md p-1 hover:bg-white/10",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring",
          )}
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      ) : null}
    </div>
  );
}
